// src/components/schedule/DeliveryNewPage.js
// Optimized rewrite of DeliveryQuoteSchedule.js.
//
// Key improvements over the original:
//   • All sub-components (DroppableDay, PanelRemeasurer, etc.) defined OUTSIDE
//     the main function — no recreation on every parent render.
//   • DeliveryCard & DriverPanel are fully memoized in separate files.
//   • useCallback on every handler passed to memo'd children.
//   • useMemo for weekStart, processed schedule data, and draggedAssignment.
//   • Removed console.log debug line.
//   • patchBoth() DRY helper for realtime subscription state patching.

import React, {
    useState, useEffect, useRef, useCallback, useMemo, memo,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    FiCalendar, FiChevronLeft, FiChevronRight,
    FiGrid, FiX, FiHash, FiUser, FiPackage, FiTruck,
    FiClock, FiLayers, FiHome, FiChevronsLeft, FiChevronsRight,
    FiMaximize, FiMinimize, FiUsers,
} from 'react-icons/fi';
import {
    DndContext, DragOverlay, useSensor, useSensors, PointerSensor,
    pointerWithin, MeasuringStrategy, useDndContext, defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { differenceInWeeks, startOfWeek } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DatePickerStyles.css';

import ConfirmationPopup from '../common/ConfirmationPopup';
import EditAssignment from '../assignments/EditAssignment';
import useDeviceDetection from '../common/useDeviceDetection';
import { DayColumnSkeleton } from '../common/SkeletonLoader';
import Toast from '../common/Toast';
import BackButton from '../common/BackButton';
import AssignmentContextMenu from './components/AssignmentContextMenu';
import OnHoldPanel from './components/OnHoldPanel';

import { supabase } from '../../config/supabase';
import {
    getDeliveriesForDate,
    getUnscheduledDeliveries,
    updateDeliveryDate,
} from '../../lib/supabase/deliverySchedule';
import {
    getCompanyDrivers,
    assignDriverToDelivery,
    removeDriverFromDelivery,
    getDriverWorkload,
} from '../../lib/supabase/schedule-driver';
import {
    subscribeToJobQuotesUpdates,
    unsubscribeFromJobUpdates,
    subscribeToJobUpdates,
    subscribeToJobDeliveriesUpdates,
    subscribeToProductionUpdates,
} from '../../lib/supabase/realtime';
import { forceFinish } from '../../lib/supabase/force-finish';
import { sendDriverSMS } from '../../lib/supabase/sms';
import { Truck } from 'lucide-react';

import DeliveryCard from './delivery/DeliveryCard';
import DriverPanel from './delivery/DriverPanel';
import {
    DAYS,
    getAustralianDate,
    getWeekStart,
    getWeekLabel,
    getMonthLabel,
    getDayDate,
    getDayDateObject,
    isDateInPast,
    isDayToday,
    isDateDisabled,
    getQuoteTypeStyle,
    getMainStatusColor,
    sortByTimeSlot,
    matchesSearch,
} from './delivery/deliveryUtils';

// ─── useDebounce ──────────────────────────────────────────────────────────────

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(id);
    }, [value, delay]);
    return debouncedValue;
};

// ─── PanelRemeasurer ──────────────────────────────────────────────────────────
// Remeasures droppable containers after the driver panel animation completes.

const PanelRemeasurer = ({ isPanelOpen }) => {
    const { measureDroppableContainers } = useDndContext();
    useEffect(() => {
        if (!isPanelOpen || !measureDroppableContainers) return;
        const id = setTimeout(() => measureDroppableContainers(), 600);
        return () => clearTimeout(id);
    }, [isPanelOpen, measureDroppableContainers]);
    return null;
};

// ─── DroppableDatePickerZone ──────────────────────────────────────────────────

const DroppableDatePickerZone = memo(({ activeId, children }) => {
    const { isOver, setNodeRef } = useDroppable({ id: 'date-picker-drop-zone' });
    return (
        <div
            ref={setNodeRef}
            className={`flex-1 text-center min-w-0 transition-all ${isOver ? 'ring-4 ring-teal-400 ring-opacity-50 bg-teal-50 rounded-lg' : ''
                }`}
        >
            {children}
        </div>
    );
});
DroppableDatePickerZone.displayName = 'DroppableDatePickerZone';

// ─── DroppableUnscheduled ─────────────────────────────────────────────────────

const DroppableUnscheduled = memo(({ isPanelOpen, children }) => {
    const { isOver, setNodeRef } = useDroppable({
        id: 'unscheduled-zone',
        disabled: isPanelOpen,
    });
    return (
        <div
            ref={setNodeRef}
            className={`bg-white border-2 border-dashed ${isOver ? 'border-orange-300 bg-orange-50' : 'border-gray-300'
                } rounded-xl p-6 hover:border-orange-300 transition-colors`}
        >
            {children}
        </div>
    );
});
DroppableUnscheduled.displayName = 'DroppableUnscheduled';

// ─── DroppableDay ─────────────────────────────────────────────────────────────

const DroppableDay = memo(({
    dayKey, dayIndex, weekStart, isPanelOpen,
    isExpanded, isCollapsed, onToggleExpand, assignmentCount,
    children,
}) => {
    const { isOver, setNodeRef } = useDroppable({
        id: `day-${dayKey}`,
        disabled: isPanelOpen,
    });

    const dayIsToday = isDayToday(weekStart, dayIndex);
    const dayIsPast = isDateInPast(getDayDateObject(weekStart, dayIndex));
    const dateLabel = getDayDate(weekStart, dayIndex);
    const dayLabel = DAYS[dayIndex]?.label ?? dayKey;

    // ── Collapsed view ────────────────────────────────────────────────────────
    if (isCollapsed) {
        return (
            <div
                ref={setNodeRef}
                className={`group relative bg-white border-2 ${isOver ? 'border-teal-400 shadow-lg shadow-teal-100' : dayIsPast ? 'border-gray-300' : 'border-gray-200'
                    } rounded-2xl overflow-hidden hover:border-teal-400 hover:shadow-xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 ${dayIsPast ? 'opacity-75' : ''}`}
                onClick={() => onToggleExpand(dayKey)}
            >
                <div className={`absolute inset-0 bg-gradient-to-br ${dayIsPast ? 'from-gray-50 to-gray-100' : dayIsToday ? 'from-green-50 via-teal-50 to-blue-50' : 'from-blue-50 via-indigo-50 to-purple-50'
                    } opacity-40`} />
                <div className="relative p-4">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                            <h3 className={`font-bold text-lg mb-1 ${dayIsToday ? 'text-teal-700' : 'text-gray-800'}`}>{dayLabel}</h3>
                            <p className="text-xs text-gray-500 font-medium">{dateLabel}</p>
                        </div>
                        <button
                            onClick={(e) => { e.stopPropagation(); onToggleExpand(dayKey); }}
                            className="p-2 hover:bg-white/80 rounded-xl transition-all duration-200 group-hover:scale-110 shadow-sm"
                            title="Expand day"
                        >
                            <FiMaximize className="w-4 h-4 text-teal-600" />
                        </button>
                    </div>
                    <div className="flex items-center justify-center">
                        <div className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm shadow-md ${assignmentCount > 0
                            ? dayIsToday ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white' : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                            : 'bg-gray-100 text-gray-500'
                            }`}>
                            <FiTruck className="w-4 h-4" />
                            <span>{assignmentCount}</span>
                        </div>
                    </div>
                    {dayIsToday && (
                        <div className="absolute bottom-2 left-2">
                            <span className="bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md animate-pulse">TODAY</span>
                        </div>
                    )}
                    {dayIsPast && !dayIsToday && (
                        <div className="absolute bottom-2 left-2">
                            <span className="bg-gray-400 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md">PAST</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Normal / expanded view ────────────────────────────────────────────────
    return (
        <div
            ref={setNodeRef}
            className={`relative ${isExpanded ? 'bg-white' : 'bg-gray-50'} border-2 ${isOver ? 'border-teal-400 shadow-2xl shadow-teal-100/50' : dayIsPast ? 'border-gray-300' : 'border-gray-200'
                } rounded-2xl overflow-hidden ${isExpanded ? 'overflow-x-auto shadow-2xl' : 'overflow-y-auto'
                } hover:border-teal-300 transition-all duration-300 ${isExpanded ? '' : 'min-h-[300px] max-h-[800px]'
                } ${dayIsPast ? 'opacity-80' : ''}`}
        >
            {/* Header */}
            <div className={`relative bg-gradient-to-br ${dayIsPast ? 'from-gray-100 via-gray-50 to-gray-100'
                : dayIsToday ? 'from-green-100 via-teal-50 to-blue-100'
                    : 'from-blue-100 via-indigo-50 to-purple-100'
                } px-4 py-3 border-b-2 ${dayIsToday ? 'border-teal-300' : 'border-gray-200'} sticky top-0 z-50 shadow-md`}>
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)',
                        backgroundSize: '24px 24px',
                    }} />
                </div>
                <div className="relative flex items-center justify-between">
                    <div className="w-11" />
                    <div className="flex-1 text-center">
                        <div className="flex items-center justify-center gap-2 mb-1">
                            <h3 className={`font-bold ${isExpanded ? 'text-2xl' : 'text-base'} ${dayIsToday ? 'text-teal-700' : 'text-gray-900'} transition-all`}>
                                {dayLabel}
                            </h3>
                            {isExpanded && (
                                <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg animate-pulse">
                                    EXPANDED
                                </div>
                            )}
                        </div>
                        <p className={`text-xs font-medium ${dayIsToday ? 'text-teal-600' : 'text-gray-600'}`}>{dateLabel}</p>
                        <div className="flex items-center justify-center gap-2 mt-2">
                            {dayIsToday && (
                                <span className="bg-gradient-to-r from-green-500 to-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1 shadow-md">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                                    Today
                                </span>
                            )}
                            {dayIsPast && !dayIsToday && (
                                <span className="bg-gray-400 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">Past</span>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => onToggleExpand(isExpanded ? null : dayKey)}
                        className={`p-2 hover:bg-white/90 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-110 ${isExpanded ? 'bg-white/80' : 'bg-white/60'}`}
                        title={isExpanded ? 'Collapse day' : 'Expand day'}
                    >
                        {isExpanded
                            ? <FiMinimize className="w-5 h-5 text-purple-600" />
                            : <FiMaximize className="w-5 h-5 text-teal-600" />}
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className={`p-3 ${isExpanded ? 'flex flex-wrap gap-3 pb-4 bg-gradient-to-br from-gray-50 to-white' : 'space-y-2'}`}>
                {children}
            </div>

            {isExpanded && (
                <div className="sticky bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
            )}
        </div>
    );
});
DroppableDay.displayName = 'DroppableDay';

// ─── Drop animation ───────────────────────────────────────────────────────────

const DROP_ANIMATION = {
    duration: 200,
    easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
    sideEffects: defaultDropAnimationSideEffects({
        styles: { active: { opacity: '0.5' } },
    }),
};

// ─── DeliverySchedule (main component) ───────────────────────────────────────

const DeliverySchedule = () => {
    const { userProfile, company } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { companySlug } = useParams();
    const { isMobile } = useDeviceDetection();

    // Week navigation
    const [weekOffset, setWeekOffset] = useState(0);

    // Schedule data
    const [scheduleData, setScheduleData] = useState({});
    const [unscheduledAssignments, setUnscheduledAssignments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // DnD state
    const [activeId, setActiveId] = useState(null);
    const [dragHandleType, setDragHandleType] = useState(null); // 'date' | 'driver'
    const [draggedItemCache, setDraggedItemCache] = useState(null);
    const [dragOverWeek, setDragOverWeek] = useState(null);   // reserved
    const [switchProgress, setSwitchProgress] = useState(0);       // reserved

    // Confirmation
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [pendingMove, setPendingMove] = useState(null);

    // Edit modal
    const [editOpen, setEditOpen] = useState(false);
    const [editJobId, setEditJobId] = useState(null);

    // Mobile
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [mobileViewMode] = useState('grid'); // 'grid' | 'list'
    const [selectedDay, setSelectedDay] = useState(null);
    const [expandedDay, setExpandedDay] = useState(null);

    // Filters
    const [showOnlyMine, setShowOnlyMine] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');
    const [availableDrivers, setAvailableDrivers] = useState([]);
    const [viewingMode, setViewingMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [productionFilter, setProductionFilter] = useState(() => {
        try { return JSON.parse(localStorage.getItem('delivery_productionFilter')) || 'all'; }
        catch { return 'all'; }
    });

    const [quoteTypeFilters, setQuoteTypeFilters] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('delivery_quoteTypeFilters')) ||
                { roof: true, wall: true, floor: true, timber: true };
        } catch {
            return { roof: true, wall: true, floor: true, timber: true };
        }
    });

    // Driver panel
    const [drivers, setDrivers] = useState([]);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [currentDropTarget, setCurrentDropTarget] = useState(null);
    const [dropTargetRect, setDropTargetRect] = useState(null);
    const [driverWorkloads, setDriverWorkloads] = useState({});
    const panelRef = useRef(null);

    // Date picker
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [datePickerValue, setDatePickerValue] = useState(null);
    const [showDragDatePicker, setShowDragDatePicker] = useState(false);
    const [draggedForDatePicker, setDraggedForDatePicker] = useState(null);
    const [selectedDateFromPicker, setSelectedDateFromPicker] = useState(null);

    // Note / time slot
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
    const [currentEditingQuote, setCurrentEditingQuote] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

    const debouncedSearchQuery = useDebounce(searchQuery, 300);

    // ── Memoised derived values ───────────────────────────────────────────────

    const weekStart = useMemo(() => getWeekStart(weekOffset), [weekOffset]);

    const processedSchedule = useMemo(() => {
        const result = {};
        for (const dayKey of Object.keys(scheduleData)) {
            const items = debouncedSearchQuery.trim()
                ? scheduleData[dayKey].filter(a => matchesSearch(a, debouncedSearchQuery))
                : scheduleData[dayKey];
            result[dayKey] = sortByTimeSlot(items);
        }
        return result;
    }, [scheduleData, debouncedSearchQuery]);

    const processedUnscheduled = useMemo(() => {
        const items = debouncedSearchQuery.trim()
            ? unscheduledAssignments.filter(a => matchesSearch(a, debouncedSearchQuery))
            : unscheduledAssignments;
        return sortByTimeSlot(items);
    }, [unscheduledAssignments, debouncedSearchQuery]);

    const draggedAssignment = useMemo(() => {
        if (draggedItemCache) return draggedItemCache;
        if (!activeId) return null;
        return [
            ...Object.values(scheduleData).flat(),
            ...unscheduledAssignments,
        ].find(a => a.id === activeId) || null;
    }, [draggedItemCache, activeId, scheduleData, unscheduledAssignments]);

    // ── DnD sensors ──────────────────────────────────────────────────────────

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    // ── Persist filters ───────────────────────────────────────────────────────

    useEffect(() => {
        localStorage.setItem('delivery_productionFilter', JSON.stringify(productionFilter));
    }, [productionFilter]);

    useEffect(() => {
        localStorage.setItem('delivery_quoteTypeFilters', JSON.stringify(quoteTypeFilters));
    }, [quoteTypeFilters]);

    // ── Fetch on mount / filter change ────────────────────────────────────────

    useEffect(() => {
        if (company?.id) fetchAssignments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company, showOnlyMine, selectedUser, weekOffset, productionFilter, quoteTypeFilters]);

    useEffect(() => {
        if (company?.id) fetchDrivers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company]);

    useEffect(() => {
        if (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') {
            setAvailableDrivers([]); // placeholder
        }
    }, [userProfile]);

    // ── Realtime subscriptions ────────────────────────────────────────────────

    useEffect(() => {
        if (!company?.id) return;

        // Shared helper: patch a single item using the provided patcher fn,
        // then apply across both scheduleData and unscheduledAssignments.
        const patchBoth = (patcher) => {
            setScheduleData(prev => {
                const next = {};
                let hit = false;
                for (const key in prev) {
                    next[key] = prev[key].map(item => {
                        const patched = patcher(item);
                        if (patched !== item) hit = true;
                        return patched;
                    });
                }
                return hit ? next : prev;
            });
            setUnscheduledAssignments(prev => {
                let hit = false;
                const next = prev.map(item => {
                    const patched = patcher(item);
                    if (patched !== item) hit = true;
                    return patched;
                });
                return hit ? next : prev;
            });
        };

        const handleJobQuotesUpdate = ({ eventType, new: updated }) => {
            if (eventType !== 'UPDATE') return;
            patchBoth(item => {
                const itemQuoteId = item.quote_info?.quoteId;
                const itemDeliveryCount = item.quote_info?.delivery_count ?? 0;
                const updDeliveryCount = updated.delivery_count ?? 0;
                const matches = itemQuoteId
                    ? itemQuoteId === updated.id
                    : item.id === updated.job_id &&
                    item.quote_info?.quote_type === updated.quote_type &&
                    itemDeliveryCount === updDeliveryCount;
                if (!matches) return item;
                return {
                    ...item,
                    quote_info: {
                        ...item.quote_info,
                        delivery_note: updated.delivery_note,
                        delivery_time_slot: updated.delivery_time_slot,
                        expected_finish_date: updated.expected_delivery_date,
                        selected_units: updated.selected_units,
                        delivery_count: updated.delivery_count,
                        production_status: updated.production_status,
                        delivery_status: updated.delivery_status,
                        design_status: updated.design_status,
                        estimation_status: updated.estimation_status,
                    },
                };
            });
        };

        const handleJobsUpdate = ({ eventType, new: updated }) => {
            if (eventType !== 'UPDATE') return;
            patchBoth(item => {
                if (item.id !== updated.id) return item;
                return {
                    ...item,
                    customer_address: updated.customer_address,
                    customer_name: updated.customer_name,
                    job_status: updated.status,
                };
            });
        };

        const handleJobDeliveriesUpdate = ({ eventType, new: updated }) => {
            if (eventType !== 'UPDATE') return;

            // If the updated row carries a child_id (UUID), only match delivery rows that have
            // the same child_id.  Do NOT fall back to quote_type matching — that would
            // accidentally patch sibling deliveries of the same type on new-system jobs.
            // For old-system rows (no child_id on the event), only match rows that also
            // lack a child_id, so new-system sibling rows are never touched.
            const deliveryMatches = (d) => {
                if (updated.child_id)
                    return d.child_id === updated.child_id;
                return (
                    !d.child_id &&
                    d.quote_type === updated.quote_type &&
                    (d.delivery_count ?? 0) === (updated.delivery_count ?? 0)
                );
            };

            const patchDeliveries = (deliveries) =>
                (deliveries || []).map(d =>
                    deliveryMatches(d)
                        ? {
                            ...d,
                            delivery_status: updated.delivery_status,
                            assigned_driver_id: updated.assigned_driver_id,
                            assigned_driver_name: updated.assigned_driver_name,
                            delivery_count: updated.delivery_count,
                            delivery_date: updated.delivery_date,
                            delivered_at: updated.delivered_at,
                        }
                        : d
                );

            patchBoth(item => {
                if (item.id !== updated.job_id) return item;
                return { ...item, job_deliveries: patchDeliveries(item.job_deliveries) };
            });
        };

        const handleJobProductionUpdate = ({ eventType, new: updated }) => {
            if (eventType !== 'UPDATE') return;
            patchBoth(item => {
                if (item.production?.id !== updated.id) return item;
                return {
                    ...item,
                    production: {
                        ...item.production,
                        phase: updated.phase,
                        is_completed: updated.is_completed,
                    },
                };
            });
        };

        const quotesSubscription = subscribeToJobQuotesUpdates(company.id, handleJobQuotesUpdate);
        const deliveriesSubscription = subscribeToJobDeliveriesUpdates(company.id, handleJobDeliveriesUpdate);
        const jobsSubscription = subscribeToJobUpdates(company.id, handleJobsUpdate);
        const productionSubscription = subscribeToProductionUpdates(company.id, handleJobProductionUpdate);

        return () => {
            unsubscribeFromJobUpdates(quotesSubscription);
            unsubscribeFromJobUpdates(jobsSubscription);
            unsubscribeFromJobUpdates(deliveriesSubscription);
            unsubscribeFromJobUpdates(productionSubscription);
        };
    }, [company?.id]);

    // ── Data fetching ─────────────────────────────────────────────────────────

    const fetchAssignments = async () => {
        try {
            setIsLoading(true);
            const opts = {
                showOnlyMine: showOnlyMine ? userProfile.id : null,
                userId: selectedUser || null,
                productionFilter,
                quoteTypeFilters,
            };
            const dayPromises = DAYS.map((_, dayIndex) =>
                getDeliveriesForDate(company.id, getDayDateObject(weekStart, dayIndex), opts)
            );
            const [unscheduledRes, ...dayResults] = await Promise.all([
                getUnscheduledDeliveries(company.id, opts),
                ...dayPromises,
            ]);
            if (unscheduledRes.error) throw unscheduledRes.error;
            const organized = {};
            DAYS.forEach(({ key }, i) => { organized[key] = dayResults[i]?.data || []; });
            setScheduleData(organized);
            setUnscheduledAssignments(unscheduledRes.data || []);
        } catch (error) {
            console.error('Error fetching deliveries:', error);
            Toast.error('Error fetching deliveries');
            setScheduleData({});
            setUnscheduledAssignments([]);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchDrivers = async () => {
        try {
            const driversData = await getCompanyDrivers(company.id);
            setDrivers(driversData || []);
            const workloads = {};
            await Promise.all(
                (driversData || []).map(async (driver) => {
                    workloads[driver.id] = await getDriverWorkload(driver.id, company.id);
                })
            );
            setDriverWorkloads(workloads);
        } catch (error) {
            console.error('Error fetching drivers:', error);
            Toast.error('Error fetching drivers');
        }
    };

    // ── Driver panel ──────────────────────────────────────────────────────────

    const openPanel = useCallback(() => setIsPanelOpen(true), []);
    const closePanel = useCallback(() => {
        setIsPanelOpen(false);
        setCurrentDropTarget(null);
        setDropTargetRect(null);
    }, []);

    // ── Assign / remove driver ────────────────────────────────────────────────

    const assignDriver = async (jobId, quoteType, driverId, driverName, deliveryCount, quoteId = null) => {
        try {
            // For new-system jobs there can be multiple cards with the same job id — one per child.
            // We must match the SPECIFIC card for this quote, not just the first card for this job,
            // otherwise job_quotes (which is date-filtered) won't contain the right quote row and
            // we'd get "This quote is not available for delivery".
            const allCards = [
                ...unscheduledAssignments,
                ...Object.values(scheduleData).flat(),
            ];
            const assignment = allCards.find(a => {
                if (a.id !== jobId) return false;
                return quoteId
                    ? a.quote_info?.quoteId === quoteId                            // new-system: match by quote UUID
                    : a.quote_info?.quote_type === quoteType &&                    // old-system: match by type + count
                    (a.quote_info?.delivery_count ?? 0) === (deliveryCount ?? 0);
            });
            if (!assignment) { Toast.error(`Assignment not found: ${jobId}`); return; }

            const quoteDeliveryCount = deliveryCount ?? 0;
            // isNewSystem: determined by childUniqueId (display name) on the quote.
            // For new-system cards only match deliveries by child_id (UUID), never by
            // quote_type fallback — otherwise a sibling's delivery row could be found instead.
            const isNewSystem = !!assignment.quote_info?.childUniqueId;
            let delivery = assignment.job_deliveries?.find(d =>
                isNewSystem
                    ? d.child_id === quoteId
                    : d.quote_type === quoteType && (d.delivery_count ?? 0) === quoteDeliveryCount
            );

            // For new-system jobs the in-memory job_deliveries array only contains rows fetched
            // alongside the card's job_quotes (date-filtered). A sibling's delivery may be present
            // but this child's row may exist in the DB without being in the cache. Query directly
            // to avoid hitting the legacy unique constraint on (job_id, quote_type, delivery_count).
            if (!delivery && isNewSystem && quoteId) {
                const { data: dbDelivery } = await supabase
                    .from('job_deliveries')
                    .select('*')
                    .eq('job_id', jobId)
                    .eq('child_id', quoteId)
                    .maybeSingle();
                if (dbDelivery) delivery = dbDelivery;
            }

            const quote = assignment.job_quotes?.find(q =>
                quoteId ? q.id === quoteId : (q.quote_type === quoteType && q.is_required)
            );
            if (!quote) { Toast.error('This quote is not available for delivery'); return; }

            if (!delivery) {
                const { data, error } = await supabase
                    .from('job_deliveries')
                    .insert({
                        job_id: jobId,
                        quote_type: quoteType,
                        is_required: true,
                        delivery_status: 'not_scheduled',
                        delivery_address: assignment.customer_address,
                        delivery_contact_name: assignment.customer_name,
                        delivery_contact_phone: assignment.customer_number,
                        delivery_count: quoteDeliveryCount,
                        child_id: quoteId || null,
                        child_unique_id: quote?.child_unique_id || null,
                    })
                    .select()
                    .single();
                if (error) throw error;
                delivery = data;
            }

            await assignDriverToDelivery(
                delivery.id,
                driverId,
                driverName,
                userProfile.id,
                userProfile.full_name || userProfile.username,
                quoteId || null,
                quote?.child_unique_id || null,
            );
            await sendDriverSMS(assignment, company, driverId, quoteType);
            await fetchAssignments();
            await fetchDrivers();
        } catch (error) {
            console.error('Error assigning driver:', error);
            Toast.error('Failed to assign driver. Please try again.');
        }
    };

    const removeDriver = async (deliveryId) => {
        try {
            await removeDriverFromDelivery(deliveryId);
            await fetchAssignments();
            await fetchDrivers();
        } catch (error) {
            console.error('Error removing driver:', error);
            Toast.error('Failed to remove driver. Please try again.');
        }
    };

    // ── DnD handlers ─────────────────────────────────────────────────────────

    const handleDragStart = useCallback((event) => {
        const handleType = event.active.data.current?.handleType;
        if (handleType === 'driver') {
            setActiveId(event.active.id);
            setDragHandleType('driver');
            const assignment = event.active.data.current?.assignment;
            if (assignment) { setDraggedItemCache(assignment); openPanel(); }
        } else {
            setActiveId(event.active.id);
            setDragHandleType('date');
            const assignment = event.active.data.current?.assignment;
            if (assignment) setDraggedItemCache(assignment);
        }
    }, [openPanel]);

    const handleDragOver = useCallback((event) => {
        if (dragHandleType !== 'driver') return;
        const { over } = event;
        if (over?.id.startsWith('driver-')) {
            setCurrentDropTarget(over.id.replace('driver-', ''));
            const el = document.getElementById(over.id);
            if (el) setDropTargetRect(el.getBoundingClientRect());
        } else {
            setCurrentDropTarget(null);
            setDropTargetRect(null);
        }
    }, [dragHandleType]);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        setDragOverWeek(null);
        setSwitchProgress(0);

        const isDriverDrag = dragHandleType === 'driver' || active.data.current?.handleType === 'driver';
        const assignmentData = draggedItemCache || active.data.current?.assignment;
        setDraggedItemCache(null);

        if (isDriverDrag) {
            if (!over) { setActiveId(null); setDragHandleType(null); closePanel(); return; }

            if (over.id === 'remove-driver-zone') {
                const quoteType = assignmentData.quote_info?.quote_type;
                const quoteDeliveryCount = assignmentData.quote_info?.delivery_count ?? 0;
                const quoteId = assignmentData.quote_info?.quoteId;
                const isNewSystem = !!assignmentData.quote_info?.childUniqueId;
                const delivery = assignmentData.job_deliveries?.find(d =>
                    isNewSystem
                        ? d.child_id === quoteId
                        : d.quote_type === quoteType && (d.delivery_count ?? 0) === quoteDeliveryCount
                );
                if (delivery?.assigned_driver_id) {
                    Toast.promise(removeDriver(delivery.id), {
                        pending: 'Removing driver…',
                        success: 'Driver removed successfully!',
                        error: 'Failed to remove driver',
                    });
                }
            } else if (over.id.startsWith('driver-')) {
                const driverId = over.id.replace('driver-', '');
                const driver = drivers.find(d => d.id === driverId);
                if (driver && assignmentData) {
                    Toast.promise(
                        assignDriver(
                            assignmentData.id,
                            assignmentData.quote_info?.quote_type,
                            driver.id,
                            driver.full_name || driver.username,
                            assignmentData.quote_info?.delivery_count ?? 0,
                            assignmentData.quote_info?.quoteId || null
                        ),
                        { pending: 'Assigning driver…', success: 'Driver assigned!', error: 'Failed to assign driver' }
                    );
                }
            }
            setActiveId(null);
            setDragHandleType(null);
            closePanel();
            return;
        }

        // Date drag
        if (!over) { setActiveId(null); return; }
        if (over.id.toString().startsWith('week-selector-')) { setActiveId(null); return; }

        if (over.id === 'date-picker-drop-zone') {
            if (assignmentData) { setDraggedForDatePicker(assignmentData); setShowDragDatePicker(true); }
            return;
        }

        if (over.id === 'unscheduled-zone') {
            setPendingMove({ assignment: assignmentData, removeDate: true });
            setShowConfirmation(true);
        } else if (over.id.startsWith('day-')) {
            const dayKey = over.id.split('-')[1];
            const dayIndex = DAYS.findIndex(d => d.key === dayKey);
            if (dayIndex !== -1) {
                const newDate = new Date(getDayDateObject(weekStart, dayIndex));
                newDate.setUTCHours(10, 0, 0, 0);
                setPendingMove({ assignment: assignmentData, newDate, dayKey, isPastDate: isDateInPast(newDate) });
                setShowConfirmation(true);
            }
        }
        setActiveId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dragHandleType, draggedItemCache, weekStart, drivers, closePanel]);

    // ── Move confirm / cancel ─────────────────────────────────────────────────

    const handleConfirmMove = async () => {
        if (!pendingMove) return;
        try {
            setIsLoading(true);
            const quoteType = pendingMove.assignment.quote_info?.quote_type;
            const deliveryCount = pendingMove.assignment.quote_info?.delivery_count ?? 0;
            const quoteId = pendingMove.assignment.quote_info?.quoteId || null;
            if (!quoteType) throw new Error('Quote type not found on assignment');
            const { error } = await updateDeliveryDate(
                pendingMove.assignment.id,
                quoteType,
                pendingMove.removeDate ? null : pendingMove.newDate,
                deliveryCount,
                quoteId
            );
            if (error) throw error;
            await fetchAssignments();
        } catch (error) {
            console.error('Failed to update delivery date:', error);
            Toast.error('Failed to update delivery schedule. Please try again.');
        } finally {
            setIsLoading(false);
            setShowConfirmation(false);
            setPendingMove(null);
            setActiveId(null);
        }
    };

    const handleCancelMove = useCallback(() => {
        setShowConfirmation(false);
        setPendingMove(null);
        setActiveId(null);
        setDragOverWeek(null);
        setSwitchProgress(0);
    }, []);

    // ── Note / time slot ──────────────────────────────────────────────────────

    const handleOpenNoteModal = useCallback((assignment, quoteType, currentNote = '', deliveryCount = 0) => {
        setCurrentEditingQuote({ assignment, quoteType, deliveryCount, quoteId: assignment.quote_info?.quoteId || null });
        setNoteText(currentNote);
        setShowNoteModal(true);
    }, []);

    const handleSaveNote = async () => {
        if (!currentEditingQuote) return;
        const { assignment, quoteType, deliveryCount = 0, quoteId } = currentEditingQuote;
        setShowNoteModal(false);
        setCurrentEditingQuote(null);
        const savedNote = noteText;
        setNoteText('');
        try {
            let query = supabase.from('job_quotes').update({ delivery_note: savedNote });
            if (quoteId) {
                query = query.eq('id', quoteId);
            } else {
                query = query.eq('job_id', assignment.id).eq('quote_type', quoteType);
                query = deliveryCount === 0
                    ? query.or('delivery_count.is.null,delivery_count.eq.0')
                    : query.eq('delivery_count', deliveryCount);
            }
            const { error } = await query;
            if (error) throw error;
            Toast.success('Note saved successfully');
        } catch (error) {
            console.error('Error saving note:', error);
            Toast.error('Failed to save note');
        }
    };

    const handleOpenTimeSlotModal = useCallback((assignment, quoteType, currentSlot = null, deliveryCount = 0) => {
        setCurrentEditingQuote({ assignment, quoteType, deliveryCount, quoteId: assignment.quote_info?.quoteId || null });
        setSelectedTimeSlot(currentSlot);
        setShowTimeSlotModal(true);
    }, []);

    const handleSaveTimeSlot = async (slot) => {
        if (!currentEditingQuote) return;
        const { assignment, quoteType, deliveryCount = 0, quoteId } = currentEditingQuote;
        setShowTimeSlotModal(false);
        setCurrentEditingQuote(null);
        setSelectedTimeSlot(null);
        try {
            let query = supabase.from('job_quotes').update({ delivery_time_slot: slot });
            if (quoteId) {
                query = query.eq('id', quoteId);
            } else {
                query = query.eq('job_id', assignment.id).eq('quote_type', quoteType);
                query = deliveryCount === 0
                    ? query.or('delivery_count.is.null,delivery_count.eq.0')
                    : query.eq('delivery_count', deliveryCount);
            }
            const { error } = await query;
            if (error) throw error;
            Toast.success(slot === null ? 'Time slot cleared' : `Time slot set to ${slot}`);
        } catch (error) {
            console.error('Error saving time slot:', error);
            Toast.error('Failed to save time slot');
        }
    };

    // ── Assignment open / edit ────────────────────────────────────────────────

    const handleOpenAssignment = useCallback((assignmentId) => {
        window.open(`/${company?.slug || companySlug}/assignment/${assignmentId}`, '_blank');
    }, [company?.slug, companySlug]);

    const handleEditAssignment = useCallback((assignmentId) => {
        setEditJobId(assignmentId);
        setEditOpen(true);
    }, []);

    const handleForceFinish = useCallback(async (assignmentId, type, quoteType = null) => {
        try {
            const result = await forceFinish(assignmentId, userProfile, type, quoteType);
            if (result?.success) {
                Toast.success(result.message || 'Force finish completed successfully');
                window.location.reload();
            } else {
                Toast.error(result?.error || 'Failed to force finish');
            }
        } catch (error) {
            console.error('Error in handleForceFinish:', error);
            Toast.error('Failed to force finish');
        }
    }, [userProfile]);

    // ── Mobile ────────────────────────────────────────────────────────────────

    const handleMobileMove = useCallback((assignment, e) => {
        e.stopPropagation();
        setSelectedAssignment(assignment);
        setShowMoveModal(true);
    }, []);

    const handleMobileMoveConfirm = (targetDay) => {
        const dayIndex = DAYS.findIndex(d => d.key === targetDay);
        const newDate = new Date(getDayDateObject(weekStart, dayIndex));
        newDate.setUTCHours(10, 0, 0, 0);
        setPendingMove({
            assignment: selectedAssignment,
            newDate,
            dayKey: targetDay,
            isPastDate: isDateInPast(newDate),
        });
        setShowMoveModal(false);
        setShowConfirmation(true);
    };

    // ── Date picker ───────────────────────────────────────────────────────────

    const handleOpenDatePicker = () => {
        setDatePickerValue(weekStart);
        setShowDatePicker(true);
    };

    const handleDatePickerSelect = (date) => {
        if (!date) return;
        const today = getAustralianDate();
        const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
        const selectedWeekStart = startOfWeek(date, { weekStartsOn: 1 });
        setWeekOffset(differenceInWeeks(selectedWeekStart, currentWeekStart));
        setShowDatePicker(false);
        setDatePickerValue(null);
    };

    const handleDragDatePickerSelect = (date) => {
        if (!date || !draggedForDatePicker) return;
        setSelectedDateFromPicker(date);
    };

    const handleDropOnDatePickerDay = () => {
        if (!selectedDateFromPicker || !draggedForDatePicker) return;
        const local = new Date(selectedDateFromPicker);
        const newDate = new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate(), 10, 0, 0, 0));
        setPendingMove({ assignment: draggedForDatePicker, newDate, isPastDate: isDateInPast(newDate) });
        setShowDragDatePicker(false);
        setDraggedForDatePicker(null);
        setSelectedDateFromPicker(null);
        setActiveId(null);
        setShowConfirmation(true);
    };

    const getDragDatePickerDayClass = (date) => {
        if (!selectedDateFromPicker) return '';
        const same =
            date.getDate() === selectedDateFromPicker.getDate() &&
            date.getMonth() === selectedDateFromPicker.getMonth() &&
            date.getFullYear() === selectedDateFromPicker.getFullYear();
        return same ? 'react-datepicker__day--selected' : '';
    };

    const closeDragDatePicker = () => {
        setShowDragDatePicker(false);
        setDraggedForDatePicker(null);
        setSelectedDateFromPicker(null);
        setActiveId(null);
    };

    // ── Render helpers ────────────────────────────────────────────────────────

    const renderCard = (assignment) => (
        <AssignmentContextMenu
            key={assignment.quote_info?.quoteId || assignment.id}
            assignment={assignment}
            onOpenAssignment={handleOpenAssignment}
            onEditAssignment={handleEditAssignment}
            onForceFinish={handleForceFinish}
            page="delivery"
            userRole={userProfile?.role}
        >
            <div key={(assignment.quote_info?.quoteId || assignment.id) + '-div'}>
                <DeliveryCard
                    key={
                        assignment.quote_info?.quoteId ||
                        `${assignment.id}-${assignment.quote_info?.quote_type}-${assignment.quote_info?.delivery_count ?? 0}`
                    }
                    assignment={assignment}
                    viewingMode={viewingMode}
                    userRole={userProfile?.role}
                    isMobile={isMobile}
                    companySlug={companySlug}
                    onOpenNote={handleOpenNoteModal}
                    onOpenTimeSlot={handleOpenTimeSlotModal}
                    onMobileMove={handleMobileMove}
                />
            </div>
        </AssignmentContextMenu>
    );

    const renderEmptyDay = () => (
        <div className="text-center py-8">
            <FiTruck className="w-6 h-6 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-500">No deliveries</p>
        </div>
    );

    // ── JSX ───────────────────────────────────────────────────────────────────

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            measuring={{ droppable: { strategy: MeasuringStrategy.WhileDragging } }}
        >
            <PanelRemeasurer isPanelOpen={isPanelOpen} />

            <>
                {/* ── Header ─────────────────────────────────────────────── */}
                <div className="flex flex-col sm:items-start justify-between gap-4">
                    <div className="flex flex-row gap-2 items-center">
                        <BackButton
                            text="Back to Schedule Hub"
                            variant="ghost"
                            size="sm"
                            iconType="chevron"
                            className="text-blue-600 hover:text-blue-800"
                        />
                        <button
                            onClick={() => navigate(`/${companySlug}/schedule/delivery/finished`, {
                                state: { from: location.pathname }
                            })}
                            className="inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 focus:outline-none bg-transparent hover:bg-gray-100 text-gray-700 px-3 py-1.5 text-sm hover:text-blue-800"
                        >
                            <Truck className="w-4 h-4 mr-2" />
                            Finished Deliveries
                        </button>
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
                            <FiTruck className="w-8 h-8 text-teal-600" />
                            Delivery Schedule
                        </h1>
                        <p className="text-gray-600 mt-1">Manage and track delivery timelines</p>
                    </div>
                </div>

                {/* ── Filters ────────────────────────────────────────────── */}
                <div className="space-y-4 border-b border-gray-200 pb-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                        {userProfile?.role === 'driver' && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="showOnlyMine"
                                    checked={showOnlyMine}
                                    onChange={(e) => setShowOnlyMine(e.target.checked)}
                                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                                />
                                <label htmlFor="showOnlyMine" className="ml-2 text-sm text-gray-700">
                                    Show only my deliveries
                                </label>
                            </div>
                        )}
                        {(userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-700">Filter by driver:</label>
                                <select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-teal-500 focus:border-teal-500 min-w-[200px]"
                                >
                                    <option value="">All drivers</option>
                                    {availableDrivers.map(d => (
                                        <option key={d.id} value={d.id}>{d.username || d.full_name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Viewing mode toggle */}
                    <button
                        className={`py-1 px-3 rounded border ${viewingMode ? 'border-green-600' : 'border-red-600'}`}
                        onClick={() => setViewingMode(v => !v)}
                    >
                        Viewing Mode{' '}
                        <span className={`font-bold ${viewingMode ? 'text-green-600' : 'text-red-600'}`}>
                            {viewingMode ? 'ON' : 'OFF'}
                        </span>
                    </button>

                    {/* Production status filter */}
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">Production Status:</span>
                        <div className="flex gap-2">
                            {[
                                { value: 'all', label: 'All', active: 'bg-blue-600', inactive: 'bg-gray-100 text-gray-600' },
                                { value: 'finished', label: 'Production Finished', active: 'bg-green-600', inactive: 'bg-gray-100 text-gray-600' },
                                { value: 'not_finished', label: 'Production Pending', active: 'bg-orange-600', inactive: 'bg-gray-100 text-gray-600' },
                            ].map(({ value, label, active, inactive }) => (
                                <button
                                    key={value}
                                    onClick={() => setProductionFilter(value)}
                                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${productionFilter === value ? `${active} text-white shadow-sm` : inactive
                                        } hover:bg-gray-200`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Quote type filters */}
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">Quote Types:</span>
                        <div className="flex gap-2 flex-wrap">
                            {[
                                { key: 'roof', icon: FiHome, label: 'Roof' },
                                { key: 'wall', icon: FiLayers, label: 'Wall' },
                                { key: 'floor', icon: FiLayers, label: 'Floor' },
                                { key: 'timber', icon: FiPackage, label: 'Timber' },
                            ].map(({ key, icon: Icon, label }) => (
                                <button
                                    key={key}
                                    onClick={() => setQuoteTypeFilters(prev => ({ ...prev, [key]: !prev[key] }))}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${quoteTypeFilters[key]
                                        ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                                        : 'bg-gray-100 text-gray-400 border-2 border-gray-300 opacity-50'
                                        }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {label}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={() => {
                                setProductionFilter('all');
                                setQuoteTypeFilters({ roof: true, wall: true, floor: true, timber: true });
                            }}
                            className="ml-auto px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-all"
                        >
                            Reset Filters
                        </button>
                    </div>

                    {/* Search */}
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700 whitespace-nowrap">Search:</label>
                        <div className="relative flex-1">
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by Job ID, Customer, Driver, Quote Type…"
                                className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all text-sm"
                            />
                            <FiGrid className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <FiX className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Week Navigation ─────────────────────────────────────── */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between gap-4">
                        <button onClick={() => setWeekOffset(o => o - 4)} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium group" title="Go back 4 weeks">
                            <FiChevronsLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            <span className="hidden sm:inline">Month</span>
                        </button>
                        <button onClick={() => setWeekOffset(o => o - 1)} className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all group" title="Previous week">
                            <FiChevronLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            <span className="hidden md:inline text-sm">Week</span>
                        </button>

                        <DroppableDatePickerZone activeId={activeId}>
                            <button
                                onClick={handleOpenDatePicker}
                                className="w-full hover:bg-gray-50 rounded-lg p-2 transition-colors group"
                                title="Pick a specific week or drop assignment here"
                            >
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center justify-center gap-2">
                                        <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                            {getMonthLabel(weekStart)}
                                        </h2>
                                        {weekOffset === 0 && (
                                            <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">This Week</span>
                                        )}
                                        {weekOffset < 0 && (
                                            <span className="bg-gray-200 text-gray-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                                {Math.abs(weekOffset)} {Math.abs(weekOffset) === 1 ? 'week' : 'weeks'} ago
                                            </span>
                                        )}
                                        {weekOffset > 0 && (
                                            <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap">
                                                In {weekOffset} {weekOffset === 1 ? 'week' : 'weeks'}
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-600 font-medium group-hover:text-blue-600 transition-colors">
                                        {getWeekLabel(weekStart)}
                                    </p>
                                    <p className="text-xs text-gray-400 group-hover:text-blue-500 transition-colors">
                                        {activeId ? 'Drop here to select week' : 'Click to select date'}
                                    </p>
                                </div>
                            </button>
                        </DroppableDatePickerZone>

                        <button onClick={() => setWeekOffset(o => o + 1)} className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all group" title="Next week">
                            <span className="hidden md:inline text-sm">Week</span>
                            <FiChevronRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        </button>
                        <button onClick={() => setWeekOffset(o => o + 4)} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all font-medium group" title="Go forward 4 weeks">
                            <span className="hidden sm:inline">Month</span>
                            <FiChevronsRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
                        </button>
                    </div>

                    {weekOffset !== 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 text-center">
                            <button
                                onClick={() => setWeekOffset(0)}
                                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-medium text-sm shadow-sm flex items-center gap-2 mx-auto"
                            >
                                <FiCalendar className="w-4 h-4" />
                                Jump to This Week
                            </button>
                        </div>
                    )}
                </div>

                {/* ── Schedule Grid ───────────────────────────────────────── */}
                {isLoading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                        {DAYS.map(({ label, key }, i) => (
                            <DayColumnSkeleton key={key} dayLabel={label} dayDate={getDayDate(weekStart, i)} />
                        ))}
                    </div>
                ) : expandedDay ? (
                    <div className="space-y-6">
                        {/* Collapsed day pills */}
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 shadow-inner">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                    <FiCalendar className="w-4 h-4 text-teal-600" />
                                    Other Days
                                </h3>
                                <span className="text-xs text-gray-500 font-medium">Click any day to expand</span>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                                {DAYS.map(({ key }, dayIndex) => {
                                    if (key === expandedDay) return null;
                                    const count = (processedSchedule[key] || []).length;
                                    return (
                                        <DroppableDay
                                            key={key}
                                            dayKey={key}
                                            dayIndex={dayIndex}
                                            weekStart={weekStart}
                                            isPanelOpen={isPanelOpen}
                                            isCollapsed
                                            onToggleExpand={setExpandedDay}
                                            assignmentCount={count}
                                        />
                                    );
                                })}
                            </div>
                        </div>

                        {/* Full-width expanded day */}
                        {DAYS.map(({ key }, dayIndex) => {
                            if (key !== expandedDay) return null;
                            const assignments = processedSchedule[key] || [];
                            return (
                                <div key={key} className="animate-fadeIn">
                                    <DroppableDay
                                        dayKey={key}
                                        dayIndex={dayIndex}
                                        weekStart={weekStart}
                                        isPanelOpen={isPanelOpen}
                                        isExpanded
                                        onToggleExpand={setExpandedDay}
                                    >
                                        {assignments.length > 0
                                            ? assignments.map((a, idx) => (
                                                <div
                                                    key={a.quote_info?.quoteId || `${a.id}-${a.quote_info?.quote_type}-${a.quote_info?.delivery_count ?? 0}`}
                                                    className="flex-shrink-0 w-80 animate-slideIn"
                                                    style={{ animationDelay: `${idx * 50}ms` }}
                                                >
                                                    {renderCard(a)}
                                                </div>
                                            ))
                                            : (
                                                <div className="text-center py-16 w-full bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-dashed border-gray-300">
                                                    <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                                        <FiTruck className="w-8 h-8 text-gray-400" />
                                                    </div>
                                                    <p className="text-sm font-semibold text-gray-500 mb-1">No deliveries scheduled</p>
                                                    <p className="text-xs text-gray-400">Drop assignments here to schedule</p>
                                                </div>
                                            )
                                        }
                                    </DroppableDay>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                        {DAYS.map(({ key }, dayIndex) => {
                            const assignments = processedSchedule[key] || [];
                            return (
                                <DroppableDay
                                    key={key}
                                    dayKey={key}
                                    dayIndex={dayIndex}
                                    weekStart={weekStart}
                                    isPanelOpen={isPanelOpen}
                                    onToggleExpand={setExpandedDay}
                                >
                                    {assignments.length > 0
                                        ? assignments.map(renderCard)
                                        : renderEmptyDay()
                                    }
                                </DroppableDay>
                            );
                        })}
                    </div>
                )}

                {/* ── Unscheduled Section ────────────────────────────────── */}
                <DroppableUnscheduled isPanelOpen={isPanelOpen}>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Unscheduled Deliveries</h3>
                        <div className="flex items-center gap-2">
                            <span className="bg-orange-100 text-orange-800 text-sm font-medium px-3 py-1 rounded-full">
                                {processedUnscheduled.length} deliveries
                                {debouncedSearchQuery.trim() && (
                                    <span className="text-orange-600 ml-1">(filtered)</span>
                                )}
                            </span>
                            {!isMobile && (userProfile?.role === 'admin' || userProfile?.role === 'super_admin') && (
                                <span className="text-xs text-gray-500">Drop here to unschedule</span>
                            )}
                        </div>
                    </div>

                    {processedUnscheduled.length === 0 ? (
                        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <FiPackage className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-500 font-medium">
                                {debouncedSearchQuery.trim()
                                    ? 'No matching unscheduled deliveries'
                                    : 'All deliveries have been scheduled'}
                            </p>
                            {debouncedSearchQuery.trim() && (
                                <p className="text-sm text-gray-400 mt-1">Try a different search term</p>
                            )}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {processedUnscheduled.map(a => renderCard(a))}
                        </div>
                    )}
                </DroppableUnscheduled>
            </>

            {/* ── Mobile Move Modal ────────────────────────────────────── */}
            {showMoveModal && (
                <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-t-xl sm:rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden">
                        <div className="flex items-center justify-between p-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Move Delivery</h3>
                            <button onClick={() => setShowMoveModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4">
                            {selectedAssignment && (
                                <div className="mb-4 bg-gray-50 p-3 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FiHash className="w-4 h-4 text-gray-400" />
                                        <span className="font-medium">{selectedAssignment.unique_id}</span>
                                        {selectedAssignment.quote_info?.quote_type && (() => {
                                            const qs = getQuoteTypeStyle(selectedAssignment.quote_info.quote_type);
                                            const Ic = qs.icon;
                                            return (
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${qs.bgColor} ${qs.textColor}`}>
                                                    <Ic className="w-3 h-3" />{qs.label}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <FiUser className="w-3 h-3" />
                                        <span>{selectedAssignment.customer_name}</span>
                                    </div>
                                </div>
                            )}
                            <h4 className="font-medium text-gray-900 mb-3">{getWeekLabel(weekStart)}</h4>
                            <div className="grid grid-cols-2 gap-2">
                                {DAYS.map(({ label, key }, dayIndex) => {
                                    const targetDate = getDayDateObject(weekStart, dayIndex);
                                    const isPast = isDateInPast(targetDate);
                                    const isCurrentDayToday = isDayToday(weekStart, dayIndex);
                                    return (
                                        <button
                                            key={key}
                                            onClick={() => handleMobileMoveConfirm(key)}
                                            className={`p-3 text-center border-2 rounded-lg transition-all ${isPast ? 'border-gray-300 bg-gray-50 opacity-60'
                                                : isCurrentDayToday ? 'border-green-300 bg-green-50 hover:border-green-400 hover:bg-green-100'
                                                    : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50'
                                                }`}
                                        >
                                            <div className="font-semibold text-sm">{label}</div>
                                            <div className="text-xs text-gray-600 mt-1">{getDayDate(weekStart, dayIndex)}</div>
                                            {isCurrentDayToday && (
                                                <span className="mt-1 block bg-green-100 text-green-700 text-[10px] font-semibold px-1.5 py-0.5 rounded">Today</span>
                                            )}
                                            {isPast && !isCurrentDayToday && (
                                                <span className="mt-1 block bg-gray-200 text-gray-600 text-[10px] font-semibold px-1.5 py-0.5 rounded">Past</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Confirmation Popup ───────────────────────────────────── */}
            <ConfirmationPopup
                isOpen={showConfirmation}
                title="Confirm Delivery Schedule Change"
                message={pendingMove
                    ? `Are you sure you want to reschedule delivery for "${pendingMove.assignment.unique_id}" to ${pendingMove?.newDate
                        ? pendingMove.newDate.toLocaleDateString('en-AU')
                        : 'unscheduled'
                    }?`
                    : ''}
                confirmText="Reschedule Delivery"
                cancelText="Cancel"
                onConfirm={handleConfirmMove}
                onCancel={handleCancelMove}
            />

            {/* ── Date Picker Modal ────────────────────────────────────── */}
            {showDatePicker && (
                <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-teal-100">
                            <div>
                                <h3 className="text-xl font-bold text-gray-900">Select Week</h3>
                                <p className="text-sm text-gray-600 mt-1">Choose any date to jump to that week</p>
                            </div>
                            <button onClick={() => setShowDatePicker(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-6 flex justify-center">
                            <DatePicker
                                selected={datePickerValue}
                                onChange={handleDatePickerSelect}
                                inline
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                calendarStartDay={1}
                                dateFormat="dd/MM/yyyy"
                                highlightDates={[getAustralianDate()]}
                                filterDate={isDateDisabled}
                                className="date-picker-custom"
                            />
                        </div>
                        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                            <button onClick={() => setShowDatePicker(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors font-medium">Cancel</button>
                            {weekOffset !== 0 && (
                                <button
                                    onClick={() => { setWeekOffset(0); setShowDatePicker(false); }}
                                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2"
                                >
                                    <FiCalendar className="w-4 h-4" />
                                    Jump to This Week
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Drag-to-date Picker Modal ────────────────────────────── */}
            {showDragDatePicker && draggedForDatePicker && (
                <div className="mobile-dialog-shell fixed inset-0 flex items-center justify-center z-[60] p-4" style={{ pointerEvents: 'auto' }}>
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeDragDatePicker} />
                    <div className="relative bg-white rounded-xl shadow-2xl overflow-hidden max-w-md w-full">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100">
                            <div className="flex-1 min-w-0 mr-4">
                                <h3 className="text-xl font-bold text-gray-900 mb-1">Schedule Delivery</h3>
                                <p className="text-sm text-gray-600 truncate">
                                    Dragging: <span className="font-semibold">{draggedForDatePicker.unique_id}</span>
                                </p>
                            </div>
                            <button onClick={closeDragDatePicker} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors flex-shrink-0">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="p-6 flex justify-center bg-gray-50">
                            <DatePicker
                                onChange={handleDragDatePickerSelect}
                                inline
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                calendarStartDay={1}
                                dateFormat="dd/MM/yyyy"
                                dayClassName={getDragDatePickerDayClass}
                                filterDate={isDateDisabled}
                                className="date-picker-custom"
                            />
                        </div>
                        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-white gap-3">
                            <button onClick={closeDragDatePicker} className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium">
                                Cancel
                            </button>
                            <button
                                onClick={handleDropOnDatePickerDay}
                                disabled={!selectedDateFromPicker}
                                className={`flex-1 px-4 py-2 rounded-lg transition-all font-medium flex items-center justify-center gap-2 ${selectedDateFromPicker
                                    ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 shadow-lg'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                <FiCalendar className="w-4 h-4" />
                                {selectedDateFromPicker
                                    ? `Schedule for ${selectedDateFromPicker.toLocaleDateString('en-AU')}`
                                    : 'Select a Date'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Driver Assignment Panel ──────────────────────────────── */}
            <DriverPanel
                isPanelOpen={isPanelOpen}
                draggedItemCache={draggedItemCache}
                drivers={drivers}
                driverWorkloads={driverWorkloads}
                currentDropTarget={currentDropTarget}
                onClose={closePanel}
                panelRef={panelRef}
            />

            {/* ── Drag Overlay ─────────────────────────────────────────── */}
            <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={DROP_ANIMATION}>
                {draggedAssignment ? (() => {
                    const qs = getQuoteTypeStyle(draggedAssignment.quote_info?.quote_type);
                    const Ic = qs.icon;
                    return (
                        <div className="bg-white border-2 border-teal-500 rounded-lg p-3 shadow-2xl pointer-events-none w-64" style={{ opacity: 0.9 }}>
                            <div className={`w-full h-1 rounded-full mb-3 ${getMainStatusColor(draggedAssignment.main_status)}`} />
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <FiHash className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                    <span className="font-semibold text-gray-900 text-sm truncate">{draggedAssignment.unique_id}</span>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${qs.bgColor} ${qs.textColor} flex-shrink-0`}>
                                        <Ic className="w-3 h-3" />{qs.label}
                                    </span>
                                </div>
                                {(draggedAssignment.urgent_quote || draggedAssignment.urgent_design) && (
                                    <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded-full">URGENT</span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                <FiUser className="w-3 h-3 text-teal-500 flex-shrink-0" />
                                <span className="text-sm text-gray-700 truncate font-medium">{draggedAssignment.customer_name}</span>
                            </div>
                        </div>
                    );
                })() : null}
            </DragOverlay>

            {/* ── Note Modal ───────────────────────────────────────────── */}
            {showNoteModal && createPortal(
                <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-900">Delivery Note</h3>
                                <button onClick={() => { setShowNoteModal(false); setCurrentEditingQuote(null); setNoteText(''); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                    <FiX className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                            {currentEditingQuote && (
                                <p className="text-sm text-gray-600 mt-2">
                                    Job: <span className="font-semibold">{currentEditingQuote.assignment.unique_id}</span>
                                    {' – '}
                                    <span className="capitalize">{currentEditingQuote.quoteType?.replace('_', ' ')}</span>
                                </p>
                            )}
                        </div>
                        <div className="p-6">
                            <textarea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                placeholder="Enter delivery note…"
                                className="w-full h-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                autoFocus
                            />
                        </div>
                        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                            <button onClick={() => { setShowNoteModal(false); setCurrentEditingQuote(null); setNoteText(''); }} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium">Cancel</button>
                            <button onClick={handleSaveNote} className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium">Save Note</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ── Time Slot Modal ──────────────────────────────────────── */}
            {showTimeSlotModal && createPortal(
                <div className="mobile-dialog-shell fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                        <div className="p-6 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-900">Delivery Time Slot</h3>
                                <button onClick={() => { setShowTimeSlotModal(false); setCurrentEditingQuote(null); setSelectedTimeSlot(null); }} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                                    <FiX className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                            {currentEditingQuote && (
                                <p className="text-sm text-gray-600 mt-2">
                                    Job: <span className="font-semibold">{currentEditingQuote.assignment.unique_id}</span>
                                    {' – '}
                                    <span className="capitalize">{currentEditingQuote.quoteType?.replace('_', ' ')}</span>
                                </p>
                            )}
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 gap-3">
                                {['First Delivery', 'Second Delivery', 'Before Lunch', 'Lunchtime', 'After Lunch', 'Last Delivery'].map((slot) => (
                                    <button
                                        key={slot}
                                        onClick={() => handleSaveTimeSlot(slot)}
                                        className={`p-4 rounded-lg border-2 transition-all text-left font-medium ${selectedTimeSlot === slot ||
                                            currentEditingQuote?.assignment?.quote_info?.delivery_time_slot === slot
                                            ? 'border-purple-600 bg-purple-50 text-purple-900'
                                            : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-lg">{slot}</span>
                                            <FiClock className="w-5 h-5" />
                                        </div>
                                    </button>
                                ))}
                                {currentEditingQuote?.assignment?.quote_info?.delivery_time_slot && (
                                    <button
                                        onClick={() => handleSaveTimeSlot(null)}
                                        className="p-4 rounded-lg border-2 border-red-200 hover:border-red-300 hover:bg-red-50 transition-all text-left font-medium text-red-700"
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="text-lg">Clear Time Slot</span>
                                            <FiX className="w-5 h-5" />
                                        </div>
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-200 flex justify-end">
                            <button onClick={() => { setShowTimeSlotModal(false); setCurrentEditingQuote(null); setSelectedTimeSlot(null); }} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg font-medium">Close</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ── On-Hold Panel ────────────────────────────────────────── */}
            <OnHoldPanel
                page="delivery"
                quoteType="delivery"
                companyId={company?.id}
                userId={userProfile?.id}
                showOnlyMine={showOnlyMine}
            />

            {/* ── Edit Assignment Modal ─────────────────────────────────── */}
            {editOpen && editJobId && (
                <EditAssignment
                    modalJobId={editJobId}
                    modalCompanySlug={companySlug}
                    onClose={() => { setEditOpen(false); setEditJobId(null); }}
                />
            )}
        </DndContext>
    );
};

export default DeliverySchedule;
